/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Button, ButtonBase, Link } from '@mui/material';
import { ReactComponent as ExternalLinkIcon } from '../../../../shared/assets/svg/external_link.svg';
import { FC, useState } from 'react';
import { useAtomValue } from 'jotai';
import useNotifications from '../../../hooks/use-notifications';
import { QSButton, QSButtonBase, QSTypography } from '../../atoms';
import TrainingVideoTile from '../../molecules/training-video-tile';
import ConfirmModal from '../../molecules/confirm-modal';
import { businessProfileAtom } from '../../../states/business-profile';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import { shouldHideDTDetails } from '../../../../modules/login/v1/utility';
import thumbnailImg from '../../../../shared/assets/img/disabled_account_thumbnail.png';
import QSTooltip from '../../molecules/tooltip';
import StepRenderer, { DataItem } from './step-renderer';

const data: DataItem[] = [
  {
    id: 1,
    title: 'Go to Meta Business Support and log into your account.',
    titleKey: 'log_into_meta_business_acc',
  },
  {
    id: 2,
    title: 'Find the WhatsApp account restriction notice.',
    titleKey: 'find_restriction_notice',
  },
  {
    id: 3,
    title: 'Click "Request Review" to start appealing the restriction.',
    titleKey: 'click_req_review',
    message: {
      title: 'Use this message',
      titleKey: 'click_req_review_msg_title',
      text: 'We are a legitimate organization; we are not violating any rules set by META. Kindly activate this account as soon as possible. Our Website: <span>[Your Website]</span>.',
      textToCopy:
        'We are a legitimate organization; we are not violating any rules set by META. Kindly activate this account as soon as possible. Our Website: ',
      textKey: 'click_req_review_msg_text',
    },
  },
  {
    id: 4,
    title: 'Submit the request and await Meta’s response.',
    titleKey: 'submit_req',
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  wabaName: string;
  businessId: string;
  wabaId: string;
}

const RestrictionErrorModal: FC<Props> = ({ open, onClose, businessId, wabaId, wabaName }) => {
  const { data: businessProfileData } = useAtomValue(businessProfileAtom);

  const renderModalTitle = () => {
    return (
      <Box
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <Box
          css={css`
            display: flex;
            width: 40px;
            height: 40px;
            padding: 3px 5px;
            justify-content: center;
            align-items: center;
            gap: 10px;
            border-radius: 50%;
            background: rgba(221, 94, 86, 0.1);
          `}
        >
          <DoDisturbAltIcon
            css={css`
              width: 24px;
              height: 24px;
              color: #dd5e56;
            `}
          />
        </Box>
        <QSTypography
          css={css`
            color: #2d2d2d;
            font-size: 18px;
            font-weight: 700;
            line-height: 27px;
          `}
        >
          Your account has been disabled by Meta
        </QSTypography>
      </Box>
    );
  };

  const renderModalContent = () => {
    return (
      <Box
        css={css`
          display: flex;
          padding: 16px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 16px;
          align-self: stretch;
          height: 100%;
        `}
      >
        <TrainingVideoTile
          videoUrl={`https://youtu.be/3eVfPGfnIZ4`}
          thumbnailUrl={thumbnailImg}
          videoContainerStyles={css`
            width: 100%;
          `}
          thumbnailStyles={css`
            height: 240px;
          `}
          playBtnTitle="Watch how"
          playBtnTitleWidth={90}
        />
        <Box
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
          `}
        >
          <QSTypography
            css={css`
              align-self: stretch;
              color: #2d2d2d;
              font-family: Inter;
              font-size: 20px;
              font-weight: 600;
              line-height: 160%;
            `}
          >
            {wabaName} account disabled by Meta
          </QSTypography>
          <QSTypography
            css={css`
              color: #2d2d2d;
              font-size: 14px;
              font-weight: 400;
              line-height: 160%;
            `}
          >
            Meta has disabled your account for not adhering to their guidelines. Follow these steps to request a review
            and regain access:
          </QSTypography>
        </Box>
        {data.map((item) => {
          return (
            <StepRenderer
              item={item}
              key={item.id}
              website={item.id === 3 ? businessProfileData?.websites?.[0] : undefined}
            />
          );
        })}
      </Box>
    );
  };

  const renderActions = () => {
    return (
      <>
        <Button
          css={css`
            display: flex;
            height: 40px;
            padding: 12px 16px;
            justify-content: center;
            align-items: center;
            gap: 8px;
            border-radius: 4px;
            border: 1px solid #e0e0e9;
            background: #fbfbfc;
            box-shadow: 0px 1px 4px 0px rgba(17, 17, 17, 0.05);
          `}
          onClick={() => window.open('https://www.whatsapp.com/legal/business-terms/', '_blank')}
        >
          <QSTypography
            css={css`
              color: var(--Neutral-Primary, #2d2d2d);
              font-family: Inter;
              font-size: 14px;
              font-weight: 600;
            `}
          >
            Read Meta’s guidelines
          </QSTypography>
        </Button>
        <QSButton
          css={css`
            display: flex;
            height: 40px;
            padding: 12px 16px;
            justify-content: center;
            align-items: center;
            gap: 8px;
            border-radius: 4px;
            background: #3f906d;
            box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
              0px 3px 1px -2px rgba(0, 0, 0, 0.2);
          `}
          onClick={() =>
            window.open(`https://business.facebook.com/business-support-home/${businessId}/${wabaId}`, '_blank')
          }
        >
          <QSTypography
            css={css`
              color: #fff;
              font-family: Inter;
              font-size: 14px;
              font-weight: 600;
            `}
          >
            Request a review
          </QSTypography>
          <ExternalLinkIcon
            css={css`
              width: 16px;
              height: 16px;
              * {
                stroke: #fff;
              }
            `}
          />
        </QSButton>
      </>
    );
  };

  const hideDTDetails = shouldHideDTDetails();

  if (!open || hideDTDetails) return null;

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      title={renderModalTitle()}
      message={renderModalContent()}
      dialogProps={{
        PaperProps: {
          sx: {
            maxWidth: '550px',
          },
        },
      }}
      contentContainerStyles={css`
        padding: 0;
      `}
      titleStyles={css`
        padding: 9px 20px;
      `}
      actions={renderActions()}
      onlyCloseWithButton
    />
  );
};

export default RestrictionErrorModal;
